import i18next from 'i18next';
import { LoginInput } from 'models/auth';
import { useCallback, useState } from 'react';
import { ADMIN_LOGIN_PATH, AUTH_TOKEN_KEY } from 'services/auth';
import { httpClient } from 'services/httpClient';
import { StorageService } from 'services/storage';
import { parseJwtToken } from 'utils/parseJwtToken';

const ADMIN_ROLE = 'SUPER_ADMIN';
const HASURA_CLAIMS = 'https://hasura.io/jwt/claims';
const HASURA_DEFAULT_ROLE = 'x-hasura-default-role';

export interface UseLoginResult {
  login: (input: LoginInput) => Promise<void>;
  loading: boolean;
}

export function useLogin(): UseLoginResult {
  const [loading, setLoading] = useState(false);

  const login = useCallback(async (input: LoginInput) => {
    setLoading(true);

    try {
      const { local } = input;

      const response = await httpClient.post(ADMIN_LOGIN_PATH, {
        email: local?.email,
        password: local?.password
      });
      const token: string | undefined = response.data?.data?.token || undefined;

      if (!token) {
        throw new Error('Invalid login response');
      }

      const decodedToken = parseJwtToken(token);
      const role = decodedToken?.[HASURA_CLAIMS]?.[HASURA_DEFAULT_ROLE];

      if (role !== ADMIN_ROLE) {
        const message = i18next.t('common:pages.login.errorPermission');

        throw new Error(message);
      }

      StorageService.set(AUTH_TOKEN_KEY, token);

      const params = new URLSearchParams(window.location.search);
      const to = params.get('to');
      const redirectPath = to || '/users';

      window.location.href = redirectPath;
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        i18next.t('common:pages.login.error');
      throw new Error(message);
    }
  }, []);

  return { login, loading };
}
